import React, { useState, useEffect, useRef } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { templatesAPI, api } from '../services/api';
import { FileText, Plus, Search, Edit, Trash2, Copy, X, Save, Grid, List, Layers, Mail, Sparkles } from 'lucide-react';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import TemplateBuilder from '../components/TemplateBuilder';
import { useOnboarding } from '../contexts/OnboardingContext';
import './TemplatesPage.css';

// Custom Table Blot - treat entire table as atomic block
const BlockEmbed = Quill.import('blots/block/embed');

// Helper to clean table HTML (remove Google Sheets junk)
const cleanTableHTML = (html) => {
  const temp = document.createElement('div');
  temp.innerHTML = html;
  const table = temp.querySelector('table');

  if (!table) return html;

  // Remove unnecessary attributes
  const attributesToRemove = ['xmlns', 'data-sheets-root', 'data-sheets-baot', 'cellspacing', 'cellpadding', 'dir'];
  attributesToRemove.forEach(attr => table.removeAttribute(attr));

  // Clean all child elements (remove inline styles, keep structure)
  const cleanElement = (element) => {
    // Remove data-sheets attributes and most inline styles
    Array.from(element.attributes).forEach(attr => {
      if (attr.name.startsWith('data-sheets-') || attr.name === 'style' || attr.name === 'class') {
        element.removeAttribute(attr.name);
      }
    });

    // Recursively clean children
    Array.from(element.children).forEach(child => cleanElement(child));
  };

  // Remove colgroup (column widths - not needed)
  const colgroup = table.querySelector('colgroup');
  if (colgroup) colgroup.remove();

  // Clean all child elements
  cleanElement(table);

  // Set simple border attribute
  table.setAttribute('border', '1');

  return table.outerHTML;
};

class TableBlot extends BlockEmbed {
  static blotName = 'table';
  static tagName = 'table';
  static className = 'ql-table';

  static create(value) {
    const node = super.create();
    if (typeof value === 'string') {
      // Clean the table HTML before inserting
      const cleanedHTML = cleanTableHTML(value);
      const temp = document.createElement('div');
      temp.innerHTML = cleanedHTML;
      const table = temp.querySelector('table');

      if (table) {
        // Copy cleaned attributes and content
        Array.from(table.attributes).forEach(attr => {
          node.setAttribute(attr.name, attr.value);
        });
        node.innerHTML = table.innerHTML;
      } else {
        node.innerHTML = cleanedHTML;
      }
    }
    return node;
  }

  static value(node) {
    // Return the entire table HTML as the value
    return node.outerHTML;
  }
}

Quill.register(TableBlot, true);

const BASIC_PLACEHOLDERS = [
  { label: 'Business Name', value: '{business_name}' },
  { label: 'Industry', value: '{industry}' },
  { label: 'Location', value: '{location}' }
];

const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    ['link'],
    ['clean']
  ],
  clipboard: {
    matchVisual: false,
    matchers: [
      // Custom matcher for tables - convert entire table to TableBlot
      ['TABLE', (node, delta) => {
        const Delta = Quill.import('delta');
        const tableHTML = node.outerHTML;
        return new Delta().insert({ table: tableHTML });
      }],
      // Prevent individual table elements from being processed separately
      ['TBODY', () => new (Quill.import('delta'))()],
      ['THEAD', () => new (Quill.import('delta'))()],
      ['TR', () => new (Quill.import('delta'))()],
      ['TD', () => new (Quill.import('delta'))()],
      ['TH', () => new (Quill.import('delta'))()]
    ]
  }
};

const TYPE_LABELS = {
  email: 'Email',
  sms: 'SMS',
  follow_up: 'Follow-up'
};

const appendPlaceholderValue =(currentValue, placeholder) => {
  const trimmed = (currentValue || '').trim();
  return trimmed ? `${trimmed} ${placeholder}` : placeholder;
};

function TemplatesPage() {
  const { refresh: refreshOnboarding } = useOnboarding();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('templatesViewMode') || 'tiles';
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAIGenerateModal, setShowAIGenerateModal] = useState(false);
  const [showBuilderModal, setShowBuilderModal] = useState(false);
  const [builderMode, setBuilderMode] = useState('create');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [builderInitialData, setBuilderInitialData] = useState(null);
  const [availableDomains, setAvailableDomains] = useState([]);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [showHtmlEditor, setShowHtmlEditor] = useState(false);
  const [showCreateChooser, setShowCreateChooser] = useState(false);
  const createButtonRef = useRef(null);
  const chooserFirstOptionRef = useRef(null);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    template_type: 'email',
    industry: '',
    subject: '',
    body: '',
    provider: 'smtp',  // Default email provider
    from_domain: '',   // Selected domain for Resend
    from_email: ''     // From email local part (e.g. "info" for info@domain.com)
  });
  const [aiGenerateForm, setAIGenerateForm] = useState({
    industry: '',
    location: '',
    template_type: 'email',
    template_structure: 'simple',
    goal: '',
    num_messages: 3,
    spacing_mode: 'ai_suggest',
    custom_spacing: ''
  });

  // Refs for cursor-position placeholder insertion
  const newSubjectRef = useRef(null);
  const editSubjectRef = useRef(null);
  const newBodyTextareaRef = useRef(null);
  const editBodyTextareaRef = useRef(null);
  const newQuillRef = useRef(null);
  const editQuillRef = useRef(null);

  const insertAtCursorOrEnd = (currentValue, placeholder, inputRef) => {
    if (inputRef && inputRef.current) {
      const el = inputRef.current;
      const start = el.selectionStart ?? currentValue.length;
      const end = el.selectionEnd ?? currentValue.length;
      const before = currentValue.substring(0, start);
      const after = currentValue.substring(end);
      const newValue = before + placeholder + after;
      // Restore cursor after state update
      setTimeout(() => {
        if (el) {
          const newPos = start + placeholder.length;
          el.setSelectionRange(newPos, newPos);
          el.focus();
        }
      }, 0);
      return newValue;
    }
    return appendPlaceholderValue(currentValue, placeholder);
  };

  const insertNewTemplatePlaceholder = (field, placeholder) => {
    if (field === 'subject') {
      setNewTemplate(prev => ({
        ...prev,
        subject: insertAtCursorOrEnd(prev.subject, placeholder, newSubjectRef)
      }));
    } else if (field === 'body') {
      // Quill editor (visual mode)
      if (newQuillRef.current && !showHtmlEditor) {
        const editor = newQuillRef.current.getEditor();
        const range = editor.getSelection(true);
        const index = range ? range.index : editor.getLength();
        editor.insertText(index, placeholder);
        return;
      }
      // Textarea (HTML or SMS mode)
      setNewTemplate(prev => ({
        ...prev,
        body: insertAtCursorOrEnd(prev.body, placeholder, newBodyTextareaRef)
      }));
    } else {
      setNewTemplate(prev => ({
        ...prev,
        [field]: appendPlaceholderValue(prev[field], placeholder)
      }));
    }
  };

  const insertEditingTemplatePlaceholder = (field, placeholder) => {
    if (field === 'subject') {
      setEditingTemplate(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          subject: insertAtCursorOrEnd(prev.subject, placeholder, editSubjectRef)
        };
      });
    } else if (field === 'body') {
      // Quill editor (visual mode)
      if (editQuillRef.current && !showHtmlEditor) {
        const editor = editQuillRef.current.getEditor();
        const range = editor.getSelection(true);
        const index = range ? range.index : editor.getLength();
        editor.insertText(index, placeholder);
        return;
      }
      // Textarea (HTML or SMS mode)
      setEditingTemplate(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          body: insertAtCursorOrEnd(prev.body, placeholder, editBodyTextareaRef)
        };
      });
    } else {
      setEditingTemplate(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          [field]: appendPlaceholderValue(prev[field], placeholder)
        };
      });
    }
  };

  const renderPlaceholderButtons = (onInsert) => (
    <div className="placeholder-helper">
      <small className="placeholder-helper-label">Available placeholders:</small>
      <div className="placeholder-buttons">
        {BASIC_PLACEHOLDERS.map((placeholder) => (
          <button
            key={placeholder.value}
            type="button"
            onClick={() => onInsert(placeholder.value)}
            className="btn-placeholder"
            title={`Insert ${placeholder.label}`}
          >
            {placeholder.value}
          </button>
        ))}
      </div>
    </div>
  );

  // Modal states
  const [errorModal, setErrorModal] = useState({ isOpen: false, title: '', message: '' });
  const [successModal, setSuccessModal] = useState({ isOpen: false, title: '', message: '' });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null });

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    localStorage.setItem('templatesViewMode', viewMode);
  }, [viewMode]);

  // Fetch domains when Resend provider is selected (create modal)
  useEffect(() => {
    if (newTemplate.provider === 'resend') {
      loadDomains();
    }
  }, [newTemplate.provider]);

  // Fetch domains when edit modal opens with Resend already selected
  useEffect(() => {
    if (editingTemplate?.provider === 'resend' && availableDomains.length === 0) {
      loadDomains();
    }
  }, [editingTemplate?.provider]);

  const loadTemplates = async () => {
    try {
      const response = await templatesAPI.getAll();
      const templateData = response.data?.templates || response.data || [];
      setTemplates(Array.isArray(templateData) ? templateData : []);
    } catch (error) {
      console.error('Error loading templates:', error);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const loadDomains = async () => {
    try {
      setLoadingDomains(true);
      // Fetch domains directly from Resend API
      const response = await api.get('/domains/from-resend');
      // Only show verified domains
      const verifiedDomains = (response.data.domains || []).filter(d => d.verification_status === 'verified');
      setAvailableDomains(verifiedDomains);
    } catch (error) {
      console.error('Error loading domains:', error);
      setAvailableDomains([]);
    } finally {
      setLoadingDomains(false);
    }
  };

  const handleCreateTemplate = async () => {
    try {
      setLoading(true);
      await templatesAPI.create(newTemplate);
      refreshOnboarding();
      setShowCreateModal(false);
      setNewTemplate({ name: '', template_type: 'email', industry: '', subject: '', body: '', provider: 'smtp', from_domain: '', from_email: '' });
      setSuccessModal({ isOpen: true, title: 'Success', message: 'Template created successfully!' });
      loadTemplates();
    } catch (error) {
      console.error('Error creating template:', error);
      setErrorModal({ isOpen: true, title: 'Error', message: 'Failed to create template. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBuilderTemplate = async (templateData) => {
    try {
      setLoading(true);
      const templateId = templateData.id || builderInitialData?.id;
      const isEdit = builderMode === 'edit' && templateId;

      if (isEdit) {
        const updatePayload = {
          name: templateData.name,
          industry: templateData.industry,
          json_config: templateData.json_config,
          type: Array.isArray(templateData.json_config?.channels) && templateData.json_config.channels.length > 1
            ? 'multi'
            : (templateData.json_config?.channels?.[0] || 'email'),
          content: ''
        };
        await templatesAPI.update(templateId, updatePayload);
        refreshOnboarding();
        setSuccessModal({ isOpen: true, title: 'Success', message: 'Multi-message template updated successfully!' });
      } else {
        await templatesAPI.createJson(templateData);
        refreshOnboarding();
        setSuccessModal({ isOpen: true, title: 'Success', message: 'Multi-message template created successfully!' });
      }

      setShowBuilderModal(false);
      setBuilderInitialData(null);
      setBuilderMode('create');
      loadTemplates();
    } catch (error) {
      console.error('Error saving JSON template:', error);
      setErrorModal({ isOpen: true, title: 'Error', message: 'Failed to save template. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (template) => {
    const messages = Array.isArray(template.json_config?.messages) ? template.json_config.messages : [];

    if (messages.length > 1) {
      setBuilderMode('edit');
      setBuilderInitialData({
        ...template,
        json_config: template.json_config,
        campaign_goal: template.json_config?.metadata?.campaign_goal || '',
        location: template.location || template.json_config?.metadata?.location || '',
        industry: template.industry || template.json_config?.metadata?.industry || ''
      });
      setShowBuilderModal(true);
      return;
    }

    const primaryMessage = messages[0];
    const resolvedType = primaryMessage?.type || template.type || template.template_type || 'email';
    const resolvedSubject = primaryMessage?.subject || template.subject || (resolvedType === 'email' ? template.name : '');
    const resolvedBody = primaryMessage?.body || template.content || template.body || '';
    const resolvedProvider = primaryMessage?.provider || template.provider || template.json_config?.metadata?.emailProvider || 'smtp';
    const resolvedFromDomain = primaryMessage?.from_domain || template.json_config?.metadata?.from_domain || '';
    const resolvedFromEmail = primaryMessage?.from_email || template.json_config?.metadata?.from_email || '';

    setEditingTemplate({
      id: template.id,
      name: template.name,
      template_type: resolvedType,
      industry: template.industry || '',
      subject: resolvedSubject,
      body: resolvedBody,
      provider: resolvedProvider,
      from_domain: resolvedFromDomain,
      from_email: resolvedFromEmail
    });
    setShowEditModal(true);
  };

  const handleUpdateTemplate = async () => {
    try {
      setLoading(true);
      const updateData = {
        name: editingTemplate.name,
        template_type: editingTemplate.template_type,
        industry: editingTemplate.industry,
        subject: editingTemplate.subject,
        body: editingTemplate.body,
        json_config: {
          messages: [{
            type: editingTemplate.template_type,
            subject: editingTemplate.subject,
            body: editingTemplate.body,
            provider: editingTemplate.provider || 'smtp',
            from_domain: editingTemplate.from_domain || '',
            from_email: editingTemplate.from_email || '',
            delay_hours: 0
          }],
          metadata: {
            emailProvider: editingTemplate.provider || 'smtp',
            from_domain: editingTemplate.from_domain || '',
            from_email: editingTemplate.from_email || ''
          }
        }
      };
      await templatesAPI.update(editingTemplate.id, updateData);
      refreshOnboarding();
      setShowEditModal(false);
      setEditingTemplate(null);
      setSuccessModal({ isOpen: true, title: 'Success', message: 'Template updated successfully!' });
      loadTemplates();
    } catch (error) {
      console.error('Error updating template:', error);
      setErrorModal({ isOpen: true, title: 'Error', message: 'Failed to update template. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicate = async (template) => {
    try {
      setLoading(true);
      const duplicateData = {
        name: `${template.name} (Copy)`,
        template_type: template.type || template.template_type,
        industry: template.industry || '',
        subject: template.subject || '',
        body: template.content || template.body || ''
      };
      await templatesAPI.create(duplicateData);
      refreshOnboarding();
      setSuccessModal({ isOpen: true, title: 'Success', message: 'Template duplicated successfully!' });
      loadTemplates();
    } catch (error) {
      console.error('Error duplicating template:', error);
      setErrorModal({ isOpen: true, title: 'Error', message: 'Failed to duplicate template. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (templateId) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Template',
      message: 'Are you sure you want to delete this template? This action cannot be undone.',
      onConfirm: () => handleDeleteConfirm(templateId)
    });
  };

  const handleDeleteConfirm = async (templateId) => {
    try {
      setLoading(true);
      await templatesAPI.delete(templateId);
      setSuccessModal({ isOpen: true, title: 'Success', message: 'Template deleted successfully!' });
      loadTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      setErrorModal({ isOpen: true, title: 'Error', message: 'Failed to delete template. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleAIGenerate = async () => {
    if (!aiGenerateForm.industry || !aiGenerateForm.location || !aiGenerateForm.goal) {
      setErrorModal({ isOpen: true, title: 'Missing Info', message: 'Please provide industry, location, and campaign goal for AI generation.' });
      return;
    }

    try {
      setLoading(true);
      const payload = {
        industry: aiGenerateForm.industry,
        location: aiGenerateForm.location,
        goal: aiGenerateForm.goal,
        template_structure: aiGenerateForm.template_structure
      };

      if (aiGenerateForm.template_structure === 'simple') {
        payload.template_type = aiGenerateForm.template_type;
      } else {
        payload.num_messages = Math.max(1, parseInt(aiGenerateForm.num_messages, 10) || 3);
        payload.spacing_mode = aiGenerateForm.spacing_mode;
        if (aiGenerateForm.spacing_mode === 'custom' && aiGenerateForm.custom_spacing.trim()) {
          const spacingValues = aiGenerateForm.custom_spacing
            .split(',')
            .map(value => parseInt(value.trim(), 10))
            .filter(value => !Number.isNaN(value));
          if (spacingValues.length === 0) {
            setErrorModal({ isOpen: true, title: 'Invalid spacing', message: 'Enter at least one valid spacing value (hours).' });
            setLoading(false);
            return;
          }
          payload.custom_spacing = spacingValues;
        }
      }

      const response = await templatesAPI.aiGenerateTemplate(payload);
      const generatedTemplate = response.data?.template || {};

      if (response.data?.structure === 'simple') {
        setNewTemplate({
          name: generatedTemplate.name || `${aiGenerateForm.industry} Template`,
          template_type: aiGenerateForm.template_type,
          industry: aiGenerateForm.industry,
          subject: generatedTemplate.subject || generatedTemplate.title || '',
          body: generatedTemplate.body || generatedTemplate.content || '',
          provider: 'smtp'  // Default provider for AI-generated templates
        });
        setShowAIGenerateModal(false);
        setShowCreateModal(true);
        setSuccessModal({ isOpen: true, title: 'Success', message: 'AI-generated template ready! Review and save below.' });
      } else {
        const builderData = {
          name: generatedTemplate.name || `${aiGenerateForm.industry} Multi-Step Sequence`,
          description: aiGenerateForm.goal,
          industry: aiGenerateForm.industry,
          location: aiGenerateForm.location,
          campaign_goal: aiGenerateForm.goal,
          json_config: {
            ...generatedTemplate,
            metadata: {
              industry: aiGenerateForm.industry,
              location: aiGenerateForm.location,
              campaign_goal: aiGenerateForm.goal
            }
          }
        };
        setBuilderInitialData(builderData);
        setBuilderMode('create');
        setShowAIGenerateModal(false);
        setShowBuilderModal(true);
        setSuccessModal({ isOpen: true, title: 'Success', message: 'AI generated a multi-message sequence. Review and customize it in the builder.' });
      }
    } catch (error) {
      console.error('Error generating template:', error);
      setErrorModal({ isOpen: true, title: 'Error', message: error.response?.data?.error || 'Failed to generate template. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  // Single "Create template" entry point: each option opens the same flow
  // the old top-level buttons opened.
  const openMultiMessageBuilder = () => {
    setBuilderMode('create');
    setBuilderInitialData(null);
    setShowBuilderModal(true);
  };

  const closeBuilderModal = () => {
    setShowBuilderModal(false);
    setBuilderInitialData(null);
    setBuilderMode('create');
  };

  const createOptions = [
    {
      key: 'multi',
      icon: Layers,
      title: 'Multi-message sequence',
      description: 'A series of emails and texts sent over time.',
      onSelect: openMultiMessageBuilder
    },
    {
      key: 'simple',
      icon: Mail,
      title: 'Simple single message',
      description: 'One email or SMS you write yourself.',
      onSelect: () => setShowCreateModal(true)
    },
    {
      key: 'ai',
      icon: Sparkles,
      title: 'AI generate',
      description: 'Describe the campaign and AI drafts it for you.',
      onSelect: () => setShowAIGenerateModal(true)
    }
  ];

  const closeCreateChooser = () => {
    setShowCreateChooser(false);
    if (createButtonRef.current) createButtonRef.current.focus();
  };

  const chooseCreateOption = (onSelect) => {
    setShowCreateChooser(false);
    onSelect();
  };

  useEffect(() => {
    if (!showCreateChooser) return undefined;
    if (chooserFirstOptionRef.current) chooserFirstOptionRef.current.focus();
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setShowCreateChooser(false);
        if (createButtonRef.current) createButtonRef.current.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showCreateChooser]);

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.subject?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesIndustry = selectedIndustry === 'all' || template.industry === selectedIndustry;
    const templateType = template.type || template.template_type;
    const matchesType = selectedType === 'all' || templateType === selectedType;
    return matchesSearch && matchesIndustry && matchesType;
  });

  const typeLabel = (type) => TYPE_LABELS[type] || (type ? String(type).toUpperCase() : 'Email');

  const renderModalHeader = ({ id, title, sub, pill, onClose }) => (
    <div className="tpl-modal-head">
      <div className="tpl-modal-heading">
        <div className="tpl-modal-title-row">
          <h2 id={id} className="tpl-modal-title">{title}</h2>
          {pill && <span className="tpl-pill">{pill}</span>}
        </div>
        {sub && <p className="tpl-modal-sub">{sub}</p>}
      </div>
      <button type="button" className="tpl-modal-close" onClick={onClose} aria-label="Close">
        <X size={18} />
      </button>
    </div>
  );

  const renderBodyEditorSwitch = () => (
    <div className="tpl-editor-switch">
      <span className="tpl-editor-switch-label">
        {showHtmlEditor ? 'HTML Editor' : 'Visual Editor'}
      </span>
      <button
        type="button"
        className="tpl-mini-btn"
        onClick={() => setShowHtmlEditor(!showHtmlEditor)}
      >
        {showHtmlEditor ? '← Visual Editor' : 'HTML Editor →'}
      </button>
    </div>
  );

  return (
    <div className="templates-page">
      <div className="page-header templates-header">
        <div className="header-content">
          <h1 className="page-title">
            <FileText className="title-icon" />
            Template Manager
          </h1>
          <p className="page-description">
            Create and manage AI-powered email and SMS templates
          </p>
        </div>
        <div className="header-actions">
          <button
            ref={createButtonRef}
            type="button"
            className="btn btn-primary tpl-create-btn"
            onClick={() => setShowCreateChooser(true)}
            aria-haspopup="dialog"
            aria-expanded={showCreateChooser}
          >
            <Plus className="tpl-btn-icon" />
            Create template
          </button>
        </div>
      </div>

      <div className="filters-section templates-toolbar">
        <div className="search-bar">
          <Search className="search-icon" />
          <input
            type="text"
            placeholder="Search templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
            aria-label="Search templates"
          />
        </div>

        <div className="filter-group">
          <select
            value={selectedIndustry}
            onChange={(e) => setSelectedIndustry(e.target.value)}
            className="filter-select"
            aria-label="Filter by industry"
          >
            <option value="all">All Industries</option>
            <option value="plumbing">Plumbing</option>
            <option value="electrical">Electrical</option>
            <option value="hvac">HVAC</option>
            <option value="landscaping">Landscaping</option>
            <option value="roofing">Roofing</option>
          </select>

          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="filter-select"
            aria-label="Filter by type"
          >
            <option value="all">All Types</option>
            <option value="email">Email</option>
            <option value="sms">SMS</option>
            <option value="follow_up">Follow-up</option>
          </select>
        </div>

        <div className="view-toggle" role="group" aria-label="View">
          <button
            type="button"
            className={`view-toggle-btn ${viewMode === 'tiles' ? 'active' : ''}`}
            onClick={() => setViewMode('tiles')}
            title="Tiles View"
            aria-label="Tiles view"
            aria-pressed={viewMode === 'tiles'}
          >
            <Grid size={16} />
          </button>
          <button
            type="button"
            className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
            title="List View"
            aria-label="List view"
            aria-pressed={viewMode === 'list'}
          >
            <List size={16} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading templates...</p>
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="empty-state">
          <FileText className="empty-icon" />
          <h3>No templates found</h3>
          <p>Create your first template to get started</p>
          <button type="button" className="btn btn-primary tpl-create-btn" onClick={() => setShowCreateChooser(true)}>
            <Plus className="tpl-btn-icon" />
            Create template
          </button>
        </div>
      ) : viewMode === 'list' ? (
        <div className="templates-list">
          <div className="list-header">
            <div className="list-col-name">Name</div>
            <div className="list-col-type">Type</div>
            <div className="list-col-industry">Industry</div>
            <div className="list-col-messages">Messages</div>
            <div className="list-col-date">Created</div>
            <div className="list-col-actions">Actions</div>
          </div>
          {filteredTemplates.map((template) => {
            const channels = template.json_config?.channels || (template.type ? [template.type] : []);
            const channelLabel = channels.length > 0
              ? channels.map((channel) => channel.toUpperCase()).join(' + ')
              : 'EMAIL';
            const messageCount = template.json_config?.messages?.length || (template.content ? 1 : 0);

            return (
              <div key={template.id} className="list-row">
                <div className="list-col-name">
                  <strong>{template.name}</strong>
                </div>
                <div className="list-col-type">
                  <span className="type-badge">{channelLabel}</span>
                </div>
                <div className="list-col-industry">
                  {template.industry || '—'}
                </div>
                <div className="list-col-messages">
                  {messageCount} {messageCount === 1 ? 'msg' : 'msgs'}
                </div>
                <div className="list-col-date">
                  {new Date(template.created_at).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </div>
                <div className="list-col-actions">
                  <button type="button" className="btn-icon-sm" title="Edit" aria-label="Edit" onClick={() => handleEdit(template)}>
                    <Edit size={16} />
                  </button>
                  <button type="button" className="btn-icon-sm" title="Duplicate" aria-label="Duplicate" onClick={() => handleDuplicate(template)}>
                    <Copy size={16} />
                  </button>
                  <button type="button" className="btn-icon-sm danger" title="Delete" aria-label="Delete" onClick={() => handleDeleteClick(template.id)}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="templates-grid">
          {filteredTemplates.map((template) => {
            const channels = template.json_config?.channels || (template.type ? [template.type] : []);
            const channelLabel = channels.length > 0
              ? channels.map((channel) => channel.toUpperCase()).join(' + ')
              : 'EMAIL';
            const messageCount = template.json_config?.messages?.length || (template.content ? 1 : 0);
            const primaryMessage = template.json_config?.messages?.[0];
            const previewSubject = primaryMessage?.subject || template.subject || template.name;
            const previewBody = primaryMessage?.body || template.content || '';

            return (
              <div key={template.id} className="template-card">
                <div className="template-header">
                  <h3 className="template-name">{template.name}</h3>
                  <div className="template-actions">
                    <button type="button" className="btn-icon-btn" title="Edit" aria-label="Edit" onClick={() => handleEdit(template)}>
                      <Edit className="icon" />
                    </button>
                    <button type="button" className="btn-icon-btn" title="Duplicate" aria-label="Duplicate" onClick={() => handleDuplicate(template)}>
                      <Copy className="icon" />
                    </button>
                    <button type="button" className="btn-icon-btn danger" title="Delete" aria-label="Delete" onClick={() => handleDeleteClick(template.id)}>
                      <Trash2 className="icon" />
                    </button>
                  </div>
                </div>

                <div className="template-meta">
                  <span className="template-type">{channelLabel}</span>
                  {messageCount > 0 && (
                    <span className="template-messages">
                      {messageCount} {messageCount === 1 ? 'message' : 'messages'}
                    </span>
                  )}
                  {template.industry && (
                    <span className="template-industry">{template.industry}</span>
                  )}
                </div>

                <div className="template-preview">
                  <p className="template-subject">{previewSubject}</p>
                  <div
                    className="template-content"
                    dangerouslySetInnerHTML={{
                      __html: previewBody.length > 300
                        ? previewBody.substring(0, 300) + '...'
                        : previewBody
                    }}
                  />
                </div>

                <div className="template-footer">
                  <span className="template-date">
                    {new Date(template.created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create chooser: one entry point for the three creation flows */}
      {showCreateChooser && (
        <div className="tpl-modal-overlay tpl-chooser-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeCreateChooser(); }}>
          <div className="tpl-modal tpl-chooser" role="dialog" aria-modal="true" aria-labelledby="tpl-chooser-title">
            {renderModalHeader({
              id: 'tpl-chooser-title',
              title: 'Create template',
              sub: 'Choose how you want to start.',
              onClose: closeCreateChooser
            })}
            <div className="tpl-chooser-options">
              {createOptions.map((option, index) => {
                const Icon = option.icon;
                return (
                  <button
                    key={option.key}
                    ref={index === 0 ? chooserFirstOptionRef : undefined}
                    type="button"
                    className="tpl-chooser-option"
                    onClick={() => chooseCreateOption(option.onSelect)}
                  >
                    <span className={`tpl-chooser-icon tpl-chooser-icon--${option.key}`}>
                      <Icon size={18} />
                    </span>
                    <span className="tpl-chooser-text">
                      <span className="tpl-chooser-title">{option.title}</span>
                      <span className="tpl-chooser-desc">{option.description}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Create Template Modal */}
      {showCreateModal && (
        <div className="tpl-modal-overlay">
          <div className="tpl-modal tpl-modal--form" role="dialog" aria-modal="true" aria-labelledby="tpl-create-title">
            {renderModalHeader({
              id: 'tpl-create-title',
              title: 'New simple template',
              sub: 'A single email or SMS message.',
              pill: typeLabel(newTemplate.template_type),
              onClose: () => setShowCreateModal(false)
            })}

            <div className="tpl-modal-body">
              <section className="tpl-section">
                <h3 className="tpl-section-title">Details</h3>
                <div className="tpl-field">
                  <label className="tpl-label">Template Name *</label>
                  <input
                    type="text"
                    className="tpl-input"
                    value={newTemplate.name}
                    onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                    placeholder="e.g., Welcome Email"
                  />
                </div>

                <div className="tpl-grid-2">
                  <div className="tpl-field">
                    <label className="tpl-label">Template Type *</label>
                    <select
                      className="tpl-input"
                      value={newTemplate.template_type}
                      onChange={(e) => setNewTemplate({ ...newTemplate, template_type: e.target.value })}
                    >
                      <option value="email">Email</option>
                      <option value="sms">SMS</option>
                      <option value="follow_up">Follow-up</option>
                    </select>
                  </div>

                  <div className="tpl-field">
                    <label className="tpl-label">Industry</label>
                    <input
                      type="text"
                      className="tpl-input"
                      value={newTemplate.industry}
                      onChange={(e) => setNewTemplate({ ...newTemplate, industry: e.target.value })}
                      placeholder="e.g., Plumbing, HVAC"
                    />
                  </div>
                </div>
              </section>

              {newTemplate.template_type === 'email' && (
                <section className="tpl-section">
                  <h3 className="tpl-section-title">Sending</h3>
                  <div className="tpl-field">
                    <label className="tpl-label">Email Provider</label>
                    <select
                      className="tpl-input"
                      value={newTemplate.provider}
                      onChange={(e) => setNewTemplate({ ...newTemplate, provider: e.target.value, from_domain: '' })}
                    >
                      <option value="smtp">SMTP</option>
                      <option value="resend">Resend</option>
                      {newTemplate.provider === 'sendgrid' && <option value="sendgrid">SendGrid</option>}
                    </select>
                    <small className="tpl-help">
                      Choose which email service to use for sending
                    </small>
                  </div>

                  {/* From Domain Dropdown - Only show when Resend is selected */}
                  {newTemplate.provider === 'resend' && (
                    <div className="tpl-field">
                      <label className="tpl-label">From Domain *</label>
                      {loadingDomains ? (
                        <div className="tpl-help tpl-help--loading">
                          Loading domains...
                        </div>
                      ) : availableDomains.length === 0 ? (
                        <div className="tpl-notice tpl-notice--warn">
                          <strong>No verified domains found.</strong>
                          <br />
                          <small>
                            Please add and verify a domain in <a href="/settings">Settings → Domains</a> before creating Resend templates.
                          </small>
                        </div>
                      ) : (
                        <>
                          <select
                            className="tpl-input"
                            value={newTemplate.from_domain}
                            onChange={(e) => setNewTemplate({ ...newTemplate, from_domain: e.target.value, from_email: '' })}
                          >
                            <option value="">Select a domain</option>
                            {availableDomains.map((domain) => (
                              <option key={domain.id} value={domain.domain}>
                                {domain.domain}
                              </option>
                            ))}
                          </select>
                          <small className="tpl-help">
                            Choose which verified domain to send from
                          </small>
                        </>
                      )}
                    </div>
                  )}

                  {/* From email address when domain is selected */}
                  {newTemplate.provider === 'resend' && newTemplate.from_domain && (
                    <div className="tpl-field">
                      <label className="tpl-label">From Email Address</label>
                      <div className="tpl-input-group">
                        <input
                          type="text"
                          className="tpl-input"
                          value={newTemplate.from_email || ''}
                          onChange={(e) => setNewTemplate({ ...newTemplate, from_email: e.target.value })}
                          placeholder="e.g. info, hello, sales"
                        />
                        <span className="tpl-input-suffix">@{newTemplate.from_domain}</span>
                      </div>
                      <small className="tpl-help">
                        Emails will send from: <strong>{newTemplate.from_email ? `${newTemplate.from_email}@${newTemplate.from_domain}` : `(choose prefix)@${newTemplate.from_domain}`}</strong>
                      </small>
                    </div>
                  )}
                </section>
              )}

              <section className="tpl-section">
                <h3 className="tpl-section-title">Message</h3>
                {newTemplate.template_type === 'email' && (
                  <div className="tpl-field">
                    <label className="tpl-label">Email Subject *</label>
                    <input
                      ref={newSubjectRef}
                      type="text"
                      className="tpl-input"
                      value={newTemplate.subject}
                      onChange={(e) => setNewTemplate({ ...newTemplate, subject: e.target.value })}
                      placeholder="e.g., Welcome to {business_name}"
                    />
                    {renderPlaceholderButtons((value) => insertNewTemplatePlaceholder('subject', value))}
                  </div>
                )}

                <div className="tpl-field">
                  <label className="tpl-label">Message Body *</label>
                  {newTemplate.template_type === 'email' ? (
                    <>
                      {renderBodyEditorSwitch()}
                      {showHtmlEditor ? (
                        <textarea
                          ref={newBodyTextareaRef}
                          className="tpl-input tpl-code"
                          value={newTemplate.body}
                          onChange={(e) => setNewTemplate({ ...newTemplate, body: e.target.value })}
                          placeholder="Paste HTML here... You can paste table HTML from sources like ChatGPT, CodePen, etc."
                          rows={15}
                        />
                      ) : (
                        <ReactQuill
                          ref={newQuillRef}
                          value={newTemplate.body}
                          onChange={(content) => setNewTemplate({ ...newTemplate, body: content })}
                          modules={quillModules}
                          placeholder="Write your template message here... Use placeholders like {business_name}, {industry}, {location}"
                          theme="snow"
                        />
                      )}
                    </>
                  ) : (
                    <textarea
                      ref={newBodyTextareaRef}
                      className="tpl-input tpl-textarea-lg"
                      value={newTemplate.body}
                      onChange={(e) => setNewTemplate({ ...newTemplate, body: e.target.value })}
                      placeholder="Write your template message here... Use placeholders like {business_name}, {industry}, {location}"
                      rows={15}
                    />
                  )}
                  {renderPlaceholderButtons((value) => insertNewTemplatePlaceholder('body', value))}
                </div>
              </section>
            </div>

            <div className="tpl-modal-foot">
              <span className="tpl-modal-hint">Fields marked * are required</span>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCreateTemplate}
                disabled={
                  loading ||
                  !newTemplate.name ||
                  !newTemplate.body ||
                  (newTemplate.provider === 'resend' && !newTemplate.from_domain)
                }
              >
                <Save size={16} />
                {loading ? 'Creating...' : 'Create Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Template Modal */}
      {showEditModal && editingTemplate && (
        <div className="tpl-modal-overlay">
          <div className="tpl-modal tpl-modal--form" role="dialog" aria-modal="true" aria-labelledby="tpl-edit-title">
            {renderModalHeader({
              id: 'tpl-edit-title',
              title: editingTemplate.name || 'Untitled template',
              sub: 'Edit template · single message',
              pill: typeLabel(editingTemplate.template_type),
              onClose: () => setShowEditModal(false)
            })}

            <div className="tpl-modal-body">
              <section className="tpl-section">
                <h3 className="tpl-section-title">Details</h3>
                <div className="tpl-field">
                  <label className="tpl-label">Template Name *</label>
                  <input
                    type="text"
                    className="tpl-input"
                    value={editingTemplate.name}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                    placeholder="e.g., Restaurant Outreach v1"
                  />
                </div>

                <div className="tpl-grid-2">
                  <div className="tpl-field">
                    <label className="tpl-label">Template Type *</label>
                    <select
                      className="tpl-input"
                      value={editingTemplate.template_type}
                      onChange={(e) => setEditingTemplate({ ...editingTemplate, template_type: e.target.value })}
                    >
                      <option value="email">Email</option>
                      <option value="sms">SMS</option>
                    </select>
                  </div>

                  <div className="tpl-field">
                    <label className="tpl-label">Industry (Optional)</label>
                    <input
                      type="text"
                      className="tpl-input"
                      value={editingTemplate.industry}
                      onChange={(e) => setEditingTemplate({ ...editingTemplate, industry: e.target.value })}
                      placeholder="e.g., Restaurants, Retail, Healthcare"
                    />
                  </div>
                </div>
              </section>

              {editingTemplate.template_type === 'email' && (
                <section className="tpl-section">
                  <h3 className="tpl-section-title">Sending</h3>
                  <div className="tpl-field">
                    <label className="tpl-label">Email Provider</label>
                    <select
                      className="tpl-input"
                      value={editingTemplate.provider || 'smtp'}
                      onChange={(e) => {
                        const newProvider = e.target.value;
                        setEditingTemplate({ ...editingTemplate, provider: newProvider, from_domain: '', from_email: '' });
                        if (newProvider === 'resend') loadDomains();
                      }}
                    >
                      <option value="smtp">SMTP</option>
                      {editingTemplate.provider === 'sendgrid' && <option value="sendgrid">SendGrid</option>}
                      <option value="resend">Resend</option>
                    </select>
                    <small className="tpl-help">
                      This template will send emails using <strong>{(editingTemplate.provider || 'smtp').toUpperCase()}</strong>
                    </small>
                  </div>

                  {/* Domain + From Email - shown when Resend is selected in edit mode */}
                  {editingTemplate.provider === 'resend' && (
                    <>
                      <div className="tpl-field">
                        <label className="tpl-label">From Domain *</label>
                        {loadingDomains ? (
                          <p className="tpl-help tpl-help--loading">Loading domains...</p>
                        ) : availableDomains.length === 0 ? (
                          <p className="tpl-notice tpl-notice--warn">No verified domains found. Add one in Settings → Domains.</p>
                        ) : (
                          <select
                            className="tpl-input"
                            value={editingTemplate.from_domain || ''}
                            onChange={(e) => setEditingTemplate({ ...editingTemplate, from_domain: e.target.value })}
                          >
                            <option value="">Select a domain</option>
                            {availableDomains.map((domain) => (
                              <option key={domain.id} value={domain.domain}>{domain.domain}</option>
                            ))}
                          </select>
                        )}
                      </div>
                      {editingTemplate.from_domain && (
                        <div className="tpl-field">
                          <label className="tpl-label">From Email Address</label>
                          <div className="tpl-input-group">
                            <input
                              type="text"
                              className="tpl-input"
                              value={editingTemplate.from_email || ''}
                              onChange={(e) => setEditingTemplate({ ...editingTemplate, from_email: e.target.value })}
                              placeholder="e.g. info, hello, sales"
                            />
                            <span className="tpl-input-suffix">@{editingTemplate.from_domain}</span>
                          </div>
                          <small className="tpl-help">
                            Emails will send from: <strong>{editingTemplate.from_email ? `${editingTemplate.from_email}@${editingTemplate.from_domain}` : `(select prefix)@${editingTemplate.from_domain}`}</strong>
                          </small>
                        </div>
                      )}
                    </>
                  )}
                </section>
              )}

              <section className="tpl-section">
                <h3 className="tpl-section-title">Message</h3>
                {editingTemplate.template_type === 'email' && (
                  <div className="tpl-field">
                    <label className="tpl-label">Email Subject *</label>
                    <input
                      ref={editSubjectRef}
                      type="text"
                      className="tpl-input"
                      value={editingTemplate.subject}
                      onChange={(e) => setEditingTemplate({ ...editingTemplate, subject: e.target.value })}
                      placeholder="e.g., Welcome to {business_name}"
                    />
                    {renderPlaceholderButtons((value) => insertEditingTemplatePlaceholder('subject', value))}
                  </div>
                )}

                <div className="tpl-field">
                  <label className="tpl-label">Message Body *</label>
                  {editingTemplate.template_type === 'email' ? (
                    <>
                      {renderBodyEditorSwitch()}
                      {showHtmlEditor ? (
                        <textarea
                          ref={editBodyTextareaRef}
                          className="tpl-input tpl-code"
                          value={editingTemplate.body}
                          onChange={(e) => setEditingTemplate({ ...editingTemplate, body: e.target.value })}
                          placeholder="Paste HTML here... You can paste table HTML from sources like ChatGPT, CodePen, etc."
                          rows={15}
                        />
                      ) : (
                        <ReactQuill
                          ref={editQuillRef}
                          value={editingTemplate.body}
                          onChange={(content) => setEditingTemplate({ ...editingTemplate, body: content })}
                          modules={quillModules}
                          placeholder="Write your template message here... Use placeholders like {business_name}, {industry}, {location}"
                          theme="snow"
                        />
                      )}
                    </>
                  ) : (
                    <textarea
                      ref={editBodyTextareaRef}
                      className="tpl-input tpl-textarea-lg"
                      value={editingTemplate.body}
                      onChange={(e) => setEditingTemplate({ ...editingTemplate, body: e.target.value })}
                      placeholder="Write your template message here... Use placeholders like {business_name}, {industry}, {location}"
                      rows={15}
                    />
                  )}
                  {renderPlaceholderButtons((value) => insertEditingTemplatePlaceholder('body', value))}
                </div>
              </section>
            </div>

            <div className="tpl-modal-foot">
              <span className="tpl-modal-hint">Fields marked * are required</span>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleUpdateTemplate}
                disabled={loading || !editingTemplate.name || !editingTemplate.body}
              >
                <Save size={16} />
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      <Modal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, title: '', message: '' })}
        title={errorModal.title}
        message={errorModal.message}
        type="error"
      />

      {/* Success Modal */}
      <Modal
        isOpen={successModal.isOpen}
        onClose={() => setSuccessModal({ isOpen: false, title: '', message: '' })}
        title={successModal.title}
        message={successModal.message}
        type="success"
      />

      {/* AI Generate Modal */}
      {showAIGenerateModal && (
        <div className="tpl-modal-overlay">
          <div className="tpl-modal tpl-modal--narrow" role="dialog" aria-modal="true" aria-labelledby="tpl-ai-title">
            {renderModalHeader({
              id: 'tpl-ai-title',
              title: 'AI generate template',
              sub: 'Describe the campaign and AI drafts the messages for you to review.',
              pill: 'AI',
              onClose: () => setShowAIGenerateModal(false)
            })}

            <div className="tpl-modal-body">
              <div className="tpl-grid-2">
                <div className="tpl-field">
                  <label className="tpl-label">Industry *</label>
                  <input
                    type="text"
                    className="tpl-input"
                    value={aiGenerateForm.industry}
                    onChange={(e) => setAIGenerateForm({ ...aiGenerateForm, industry: e.target.value })}
                    placeholder="e.g., Tree Surgeons, Plumbers, Electricians"
                  />
                </div>

                <div className="tpl-field">
                  <label className="tpl-label">Location *</label>
                  <input
                    type="text"
                    className="tpl-input"
                    value={aiGenerateForm.location}
                    onChange={(e) => setAIGenerateForm({ ...aiGenerateForm, location: e.target.value })}
                    placeholder="e.g., London, Manchester"
                  />
                </div>
              </div>

              <div className="tpl-field">
                <label className="tpl-label">Campaign Goal / Purpose *</label>
                <textarea
                  className="tpl-input"
                  value={aiGenerateForm.goal}
                  onChange={(e) => setAIGenerateForm({ ...aiGenerateForm, goal: e.target.value })}
                  placeholder="Describe what you want this campaign to achieve..."
                  rows={3}
                />
              </div>

              <div className="tpl-grid-2">
                <div className="tpl-field">
                  <label className="tpl-label">Template Structure *</label>
                  <select
                    className="tpl-input"
                    value={aiGenerateForm.template_structure}
                    onChange={(e) => setAIGenerateForm({ ...aiGenerateForm, template_structure: e.target.value })}
                  >
                    <option value="simple">Single Message</option>
                    <option value="multi">Multi-Message Sequence</option>
                  </select>
                </div>

                {aiGenerateForm.template_structure === 'simple' ? (
                  <div className="tpl-field">
                    <label className="tpl-label">Template Type *</label>
                    <select
                      className="tpl-input"
                      value={aiGenerateForm.template_type}
                      onChange={(e) => setAIGenerateForm({ ...aiGenerateForm, template_type: e.target.value })}
                    >
                      <option value="email">Email</option>
                      <option value="sms">SMS</option>
                    </select>
                  </div>
                ) : (
                  <div className="tpl-field">
                    <label className="tpl-label">Number of Messages *</label>
                    <input
                      type="number"
                      className="tpl-input"
                      min="2"
                      max="6"
                      value={aiGenerateForm.num_messages}
                      onChange={(e) => setAIGenerateForm({ ...aiGenerateForm, num_messages: e.target.value })}
                    />
                  </div>
                )}
              </div>

              {aiGenerateForm.template_structure !== 'simple' && (
                <>
                  <div className="tpl-field">
                    <label className="tpl-label">Message Spacing</label>
                    <select
                      className="tpl-input"
                      value={aiGenerateForm.spacing_mode}
                      onChange={(e) => setAIGenerateForm({ ...aiGenerateForm, spacing_mode: e.target.value })}
                    >
                      <option value="ai_suggest">Let AI suggest spacing</option>
                      <option value="custom">Custom spacing (hours)</option>
                    </select>
                  </div>

                  {aiGenerateForm.spacing_mode === 'custom' && (
                    <div className="tpl-field">
                      <label className="tpl-label">Custom Spacing (comma separated)</label>
                      <input
                        type="text"
                        className="tpl-input"
                        value={aiGenerateForm.custom_spacing}
                        onChange={(e) => setAIGenerateForm({ ...aiGenerateForm, custom_spacing: e.target.value })}
                        placeholder="e.g., 24, 48, 72"
                      />
                      <small className="tpl-help">Enter hours between each message (e.g., "24,48,72"). Leave blank to let AI decide.</small>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="tpl-modal-foot">
              <span className="tpl-modal-hint">You can review and edit before saving</span>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowAIGenerateModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAIGenerate}
                disabled={loading || !aiGenerateForm.industry || !aiGenerateForm.location}
              >
                <Sparkles size={16} />
                {loading ? 'Generating...' : 'Generate Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Builder Modal */}
      {showBuilderModal && (
        <div className="tpl-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeBuilderModal(); }}>
          <div className="tpl-modal tpl-modal--wide" role="dialog" aria-modal="true" aria-labelledby="tpl-builder-title">
            {renderModalHeader({
              id: 'tpl-builder-title',
              title: builderMode === 'edit'
                ? (builderInitialData?.name || 'Untitled template')
                : 'New multi-message template',
              sub: builderMode === 'edit'
                ? 'Edit multi-message template'
                : 'A sequence of emails and texts sent over time.',
              pill: 'Multi-message',
              onClose: closeBuilderModal
            })}
            <TemplateBuilder
              initialData={builderInitialData}
              onSave={handleSaveBuilderTemplate}
              onCancel={closeBuilderModal}
            />
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
}

export default TemplatesPage;
